import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import * as api from "../../../../api/generated-api";
import {
  configureAuthHeader,
  configureBaseUrl,
} from "../../../../api/axiosConfig";
import {
  getDealDocument,
  getDealDocumentContents,
} from "../../../../api/deals/documents";
import DealDocumentPDF from "../ViewDocument";
import { DocumentSignature } from "../../documentSignature";
import {
  getDealDocumentSignatureById,
  getDealSignaturesForUser,
  signDealDocument,
  updateDealDocumentSignatures,
} from "../../../../api/deals/signatures";
import { clickToLinkHandler } from "../../../../helpers/utils";
import { DealsPageLayout } from "../../dealsPageLayout/DealsPageLayout";
import DocumentPageContainer from "../DocumentPageContainer";
import { Box, SxProps, Theme } from "@mui/material";
import DealPageTitleAndAction from "../../common/DealPageTitleAndAction";
import { useHistory, useLocation } from "react-router-dom";
import { DealPageActionButtonInfo } from "../../dealsPageLayout/DealsPageHeader/DealsPageHeader";
import YourSignatures from "../../closing/your-signatures/YourSignatures";
import { showSignAllSuccessQueryStringParam } from "../../closing/your-signatures/YourSignaturesPage";
import { UserWithPersonWithOrganizationPersons } from "../../../../api/types";

type SignDealDocumentPageProps = {
  token: string;
  orgId: number;
  dealId: number;
  documentId: number;
  dealDocumentSignerId: number;
  userId: number;
  user: UserWithPersonWithOrganizationPersons;
};

const SignDealDocumentPage = (props: SignDealDocumentPageProps) => {
  const {
    orgId,
    dealId,
    documentId,
    dealDocumentSignerId,
    userId,
    user,
    token,
  } = props;

  configureBaseUrl();
  configureAuthHeader(token);

  const [dealDocument, setDealDocument] = useState<api.DealDocument>();
  const [dealDocumentContents, setDealDocumentContents] = useState<Blob>();
  const [dealDocumentSigner, setDealDocumentSigner] =
    useState<api.DealDocumentSignatureWithSignatureContent>();
  const [dealDocumentSignaturesForUser, setDealDocumentSignaturesForUser] =
    useState<api.DealDocumentSignature[]>();
  const [headerActionButtons, setHeaderActionButtons] = useState<
    DealPageActionButtonInfo[]
  >([]);
  const [sidebarHeaderActionButtons, setSidebarHeaderActionButtons] = useState<
    DealPageActionButtonInfo[]
  >([]);
  const [showSignAllDialog, setShowSignAllDialog] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const history = useHistory();

  //fetch page data
  useEffect(() => {
    getDealDocument(orgId, dealId, documentId, (fetchedDealDocument) => {
      setDealDocument(fetchedDealDocument);
    });

    getDealDocumentContents(orgId, dealId, documentId, (response) => {
      setDealDocumentContents(response.data);
    });

    getDealDocumentSignatureById(
      orgId,
      dealId,
      documentId,
      dealDocumentSignerId,
      (fetchedDealDocumentSigner) => {
        setDealDocumentSigner(fetchedDealDocumentSigner);
      }
    );

    getDealSignaturesForUser(
      orgId,
      dealId,
      userId,
      (fetchedDealDocumentSignatures) => {
        setDealDocumentSignaturesForUser(fetchedDealDocumentSignatures);
      }
    );
  }, [orgId, dealId, documentId]);

  //scroll to signature if query param is present
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const shouldScrollToSignature = query.get("scrollToSignature") === "true";

  const signatureRef = useRef(null);

  const handleScrollToSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  useEffect(() => {
    if (
      signatureRef.current &&
      shouldScrollToSignature &&
      dealDocumentContents &&
      dealDocumentSigner &&
      isLoaded
    ) {
      setTimeout(() => {
        //wait for the signature page to render before scrolling; this is a bit of a hack but seems to work; we could also use something more robust like react-intersection-observer if needed
        handleScrollToSignature();
      }, 100);
    }
  }, [
    signatureRef,
    shouldScrollToSignature,
    dealDocumentContents,
    dealDocumentSigner,
    isLoaded,
  ]);

  //set header action buttons
  useEffect(() => {
    const remainingSignatures = dealDocumentSignaturesForUser?.filter(
      (dds) => dds.status !== api.SigningStatus.signed
    );

    if (
      dealDocumentSigner &&
      dealDocumentSigner.status !== api.SigningStatus.signed
    ) {
      setHeaderActionButtons([
        {
          label: "Go To Signature",
          dataTestId: "go-to-signature-button",
          onClick: handleScrollToSignature,
        },
      ]);
    } else if (remainingSignatures && remainingSignatures.length > 0) {
      setHeaderActionButtons([
        {
          label: "Next Signature",
          dataTestId: "go-to-next-signature-button",
          onClick: () => {
            clickToLinkHandler(
              `/${orgId}/deals/${dealId}/documents/${remainingSignatures[0].dealDocumentId}/sign/${remainingSignatures[0].id}?scrollToSignature=true`
            );
          },
        },
      ]);
    } else {
      setHeaderActionButtons([]);
    }
  }, [dealDocumentSigner, dealDocumentSignaturesForUser]);

  //set sidebar header action buttons
  useEffect(() => {
    if (dealDocumentSignaturesForUser?.length > 0) {
      const dealDocumentSignaturesUnsigned =
        dealDocumentSignaturesForUser.filter(
          (dealSigner) => dealSigner.status === api.SigningStatus.requested
        );

      if (dealDocumentSignaturesUnsigned?.length > 0) {
        setSidebarHeaderActionButtons([
          {
            label: "Sign All",
            dataTestId: "sign-all-documents-button",
            primary: true,
            onClick: () => {
              setShowSignAllDialog(true);
            },
          },
        ]);
      } else {
        setSidebarHeaderActionButtons([]);
      }
    }
  }, [dealDocumentSignaturesForUser]);

  const handleSignAllDealDocuments = () => {
    const dealDocumentSignaturesUnsigned = dealDocumentSignaturesForUser.filter(
      (dealSigner) => dealSigner.status === api.SigningStatus.requested
    );

    //optimistically update state
    const updatedDealDocumentSignatures = dealDocumentSignaturesForUser.map(
      (dealDocumentSignature) => {
        if (dealDocumentSignature.status === api.SigningStatus.requested) {
          dealDocumentSignature.status = api.SigningStatus.signed;
        }
        return dealDocumentSignature;
      }
    );

    setDealDocumentSignaturesForUser(updatedDealDocumentSignatures);

    //update the API
    const updateValues = dealDocumentSignaturesUnsigned.map(
      (dealDocumentSignature) => {
        return {
          id: dealDocumentSignature.id,
          status: api.SigningStatus.signed,
        };
      }
    );

    updateDealDocumentSignatures(orgId, dealId, documentId, updateValues);

    //remove sign all button from right sidebar
    setSidebarHeaderActionButtons([]);

    //delay 1s
    const timer = setTimeout(() => {
      //redirect the user to the your signatures page
      history.push(
        `/${orgId}/deals/${dealId}/edit/your-signatures?${showSignAllSuccessQueryStringParam}=true`
      );
    }, 3 * 1000);

    return () => {
      clearTimeout(timer);
    };
  };

  if (!dealDocument || !dealDocumentContents) {
    return null;
  }

  const DealDocumentSignaturePage = () => {
    const signaturePageContainer: SxProps<Theme> = {
      display: "flex",
      flex: 1,
      justifyContent: "center",
    };

    return (
      <DocumentPageContainer>
        <Box sx={signaturePageContainer}>
          <a id="signature" />
          {dealDocumentSigner && (
            <DocumentSignature
              signingPerson={{
                firstName: user.person.first_name,
                lastName: user.person.last_name,
                email: user.person.email,
                id: user.person.id,
                createdAt: user.person.created_at,
                updatedAt: user.person.updated_at,
              }}
              signingStatus={dealDocumentSigner.status}
              signatureBlockContent={dealDocumentSigner.signatureBlockContent}
              onSign={async () => {
                //optimistically update the dealDocumentSigner in state
                const updatedDealDocumentSigner = {
                  ...dealDocumentSigner,
                  status: api.SigningStatus.signed,
                };
                setDealDocumentSigner(updatedDealDocumentSigner);
                //optimistically update the dealDocumentSignaturesForUser in state
                const foundIndex = dealDocumentSignaturesForUser.findIndex(
                  (dds) => dds.id === dealDocumentSigner.id
                );
                const updatedDealDocumentSignaturesForUser = [
                  ...dealDocumentSignaturesForUser,
                ];
                updatedDealDocumentSignaturesForUser[foundIndex] =
                  updatedDealDocumentSigner;
                setDealDocumentSignaturesForUser(
                  updatedDealDocumentSignaturesForUser
                );
                //call API to sign the document
                signDealDocument(
                  dealDocument.organizationId,
                  dealId,
                  dealDocument.id,
                  dealDocumentSignerId,
                  (signedDocumentSigner) => {
                    //find the index of the signedDocumentSigner in the documentSignatures array
                    const signerIndex = dealDocumentSignaturesForUser.findIndex(
                      (existingDocumentSigner) =>
                        existingDocumentSigner.id === signedDocumentSigner.id
                    );
                    //replace the existing documentSigner with the signedDocumentSigner
                    const newDocumentSignatures = [
                      ...dealDocumentSignaturesForUser,
                    ];
                    newDocumentSignatures[signerIndex] = signedDocumentSigner;
                    setDealDocumentSignaturesForUser(newDocumentSignatures);
                    setDealDocumentSigner(signedDocumentSigner);
                  }
                );
              }}
            />
          )}
        </Box>
      </DocumentPageContainer>
    );
  };

  return (
    <DealsPageLayout
      showDealNav={false}
      leftHeaderAction={
        <DealPageTitleAndAction
          dataTestid="back-to-your-signatures"
          title={dealDocument.name}
          breadcrumbs={[
            {
              dataTestId: "back-to-your-signatures",
              title: "Your Signatures",
              path: `/${orgId}/deals/${dealId}/edit/your-signatures`,
            },
          ]}
        />
      }
      actionButtons={headerActionButtons}
      sidebar={
        <YourSignatures
          orgId={orgId}
          dealId={dealId}
          dealDocumentSignatures={dealDocumentSignaturesForUser}
          selectedDealDocumentSignatureId={dealDocumentSigner?.id}
          showSignAllDialog={showSignAllDialog}
          onSignAllDealDocuments={handleSignAllDealDocuments}
          onCancelSignAllDealDocuments={() => {
            setShowSignAllDialog(false);
          }}
        />
      }
      sidebarTitle="Your Signatures"
      sidebarActionButtons={sidebarHeaderActionButtons}
      scrollable={false}
    >
      <DealDocumentPDF
        dealDocumentContents={dealDocumentContents}
        onLoaded={() => {
          setIsLoaded(true);
        }}
      >
        <Box ref={signatureRef}>
          <DealDocumentSignaturePage />
        </Box>
      </DealDocumentPDF>
    </DealsPageLayout>
  );
};

const mapStateToProps = (state, props): SignDealDocumentPageProps => {
  const {
    match: {
      params: { dealId, orgId, documentId, dealDocumentSignerId },
    },
  } = props;
  const { token } = state.auth;
  const user = state.auth.user;
  return {
    token,
    userId: user.id,
    user,
    orgId,
    dealId,
    documentId,
    dealDocumentSignerId,
  };
};

export default connect(mapStateToProps)(SignDealDocumentPage);
